import { ArrowLeftIcon, ArrowRightIcon, DiscordLogoIcon, EnvelopeSimpleIcon, GoogleLogoIcon } from '@phosphor-icons/react'
import { ApiError, describeError, type EmailStartResponse } from '@robotensor/api'
import { Button, Notice, OtpInput, ShardMark, TextField } from '@robotensor/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from 'react'
import { useSite } from '../app/site.tsx'
import { api, client } from '../lib/api.ts'
import { onboardingComplete } from '../lib/auth.tsx'

const route = getRouteApi('/signin')
const RESEND_AFTER_S = 30

export function SignInPage(): ReactElement {
  const site = useSite()
  const search = route.useSearch()
  const router = useRouter()
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState(search.email ?? '')
  const [code, setCode] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [codeError, setCodeError] = useState<{ message: string; final: boolean } | null>(null)
  const [sent, setSent] = useState<EmailStartResponse | null>(null)
  const [sentAt, setSentAt] = useState(0)
  const [now, setNow] = useState(Date.now())
  const emailRef = useRef<HTMLInputElement>(null)

  const providers = useQuery({ queryKey: ['auth', 'providers'], queryFn: api.auth.providers, staleTime: Infinity, retry: false })
  const ready = useQuery({
    queryKey: ['probe', 'platform'],
    queryFn: () => client.probe('platform'),
    refetchInterval: 30_000,
    staleTime: 10_000,
  })

  useEffect(() => {
    if (step !== 'code') return
    const t = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(t)
  }, [step])

  useEffect(() => {
    document.title = `Sign in · ${site.name}`
  }, [site.name])

  const start = useMutation({
    mutationFn: (addr: string) => api.auth.startEmail(addr),
    onSuccess: (r) => {
      setSent(r)
      setSentAt(Date.now())
      setNow(Date.now())
      setCode('')
      setCodeError(null)
      setStep('code')
    },
    onError: (e) => {
      if (e instanceof ApiError && (e.code === 'email_invalid' || e.code === 'invalid')) setEmailError('That doesn’t look like an email address. Check for typos.')
      else setEmailError(describeError(e))
    },
  })

  const verify = useMutation({
    mutationFn: (c: string) => api.auth.verifyEmail(email.trim(), c),
    onSuccess: (session) => {
      const next = search.next ?? '/home'
      if (site.onboarding && !onboardingComplete(session.user.onboarding)) void router.navigate({ to: '/welcome' as never, search: { next } as never })
      else router.history.push(next)
    },
    onError: (e) => {
      const final = e instanceof ApiError && (e.code === 'code_expired' || e.code === 'code_locked')
      setCodeError({ message: describeError(e), final })
      setCode('')
    },
  })

  const submitEmail = (e: FormEvent) => {
    e.preventDefault()
    const v = email.trim()
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError('Enter the email address you use for Robotensor.')
      emailRef.current?.focus()
      return
    }
    setEmailError(null)
    start.mutate(v)
  }

  const wait = Math.max(0, RESEND_AFTER_S - Math.floor((now - sentAt) / 1000))
  const status = ready.data
  const statusLabel = !status ? 'checking' : status.ok ? 'ready' : 'unreachable'

  return (
    <div className="c-signin">
      <header className="c-signin__top">
        <span className="c-signin__brand">
          <ShardMark size={22} />
          <span>{site.name}</span>
        </span>
      </header>

      <main className="c-signin__main" id="main">
        <div className="c-signin__form">
          {search.reason === 'expired' ? (
            <Notice tone="warn" title="Your session ended">
              Sign in again; you’ll return to the page you were on.
            </Notice>
          ) : search.reason === 'signed-out' ? (
            <Notice title="You’re signed out">Sessions on your other devices are unaffected.</Notice>
          ) : search.reason === 'provider' ? (
            <Notice tone="warn" title="That sign-in didn’t finish">
              Try again, or get a code by email instead.
            </Notice>
          ) : null}

          {step === 'email' ? (
            <form onSubmit={submitEmail} noValidate className="c-signin__step">
              <span className="rt-label">Sign in · step 1 of 2</span>
              <h1 className="c-signin__title">{site.signIn.title}</h1>
              <p className="c-signin__lede">{site.signIn.lede}</p>
              <TextField
                ref={emailRef}
                label="Email"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                autoFocus
                size="xl"
                prefix={<EnvelopeSimpleIcon />}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  if (emailError) setEmailError(null)
                }}
                error={emailError ?? undefined}
              />
              <Button type="submit" variant="primary" size="xl" block loading={start.isPending} trailingIcon={<ArrowRightIcon weight="bold" />}>
                Send code
              </Button>
              {(site.signIn.google && providers.data?.google) || (site.signIn.discord && providers.data?.discord) ? (
                <div className="c-signin__or" aria-hidden>
                  <span>or</span>
                </div>
              ) : null}
              {site.signIn.google && providers.data?.google ? (
                <Button asChild variant="secondary" size="lg" block>
                  <a href={api.auth.googleStartUrl(search.next ?? '/home')}>
                    <GoogleLogoIcon />
                    <span>Continue with Google</span>
                  </a>
                </Button>
              ) : null}
              {site.signIn.discord && providers.data?.discord ? (
                <Button asChild variant="secondary" size="lg" block>
                  <a href={api.auth.discordStartUrl(search.next ?? '/home')}>
                    <DiscordLogoIcon />
                    <span>Continue with Discord</span>
                  </a>
                </Button>
              ) : null}
            </form>
          ) : (
            <div className="c-signin__step">
              <span className="rt-label">Sign in · step 2 of 2</span>
              <h1 className="c-signin__title">Check your email</h1>
              <p className="c-signin__lede">
                We sent a code to <strong>{email.trim()}</strong>. It works for {Math.round((sent?.expires_in ?? 600) / 60)} minutes.
              </p>
              <OtpInput
                value={code}
                onChange={(v) => {
                  setCode(v)
                  if (codeError && !codeError.final) setCodeError(null)
                }}
                onComplete={(v) => !verify.isPending && verify.mutate(v)}
                invalid={!!codeError}
                disabled={verify.isPending || codeError?.final}
                autoFocus
                describedBy="otp-help"
              />
              <div id="otp-help" aria-live="polite" className="c-signin__help">
                {verify.isPending ? (
                  <span className="rt-muted">Checking…</span>
                ) : codeError ? (
                  <span className="rt-fault-text" role="alert">
                    {codeError.message}
                  </span>
                ) : (
                  <span className="rt-muted">Paste the whole code or type it; it submits by itself.</span>
                )}
              </div>
              {codeError?.final ? (
                <Button variant="primary" size="lg" block loading={start.isPending} onClick={() => start.mutate(email.trim())}>
                  Send a new code
                </Button>
              ) : null}
              {sent?.dev_code ? (
                <Notice
                  tone="warn"
                  title="Development server"
                  actions={
                    <Button size="sm" onClick={() => setCode(sent.dev_code!)} disabled={verify.isPending}>
                      Fill
                    </Button>
                  }
                >
                  This address is allow-listed for dev codes: <span className="rt-mono">{sent.dev_code}</span>. Production never returns codes.
                </Notice>
              ) : null}
              <div className="c-signin__row">
                <Button variant="quiet" icon={<ArrowLeftIcon />} onClick={() => setStep('email')}>
                  Different email
                </Button>
                <Button variant="quiet" disabled={wait > 0 || start.isPending} onClick={() => start.mutate(email.trim())}>
                  {wait > 0 ? (
                    <>
                      Resend in <span className="rt-mono">0:{String(wait).padStart(2, '0')}</span>
                    </>
                  ) : (
                    'Resend code'
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <aside className="c-signin__plate" aria-label={`About ${site.name}`}>
          <ShardMark className="c-signin__mark" />
          <dl className="c-signin__spec">
            {site.signIn.plate.map((item) => (
              <div key={item.label}>
                <dt className="rt-label">
                  {item.icon ? (
                    <span className="c-signin__icon" aria-hidden>
                      <item.icon weight="bold" />
                    </span>
                  ) : null}
                  {item.label}
                </dt>
                <dd>{item.text}</dd>
              </div>
            ))}
          </dl>
        </aside>
      </main>

      <footer className="c-signin__foot rt-mono">
        <span className="c-signin__status" data-state={statusLabel}>
          <span aria-hidden className="c-signin__dot" /> platform {statusLabel}
          {status?.ok ? ` · ${Math.round(status.ms)} ms` : ''}
        </span>
        <span>Codes are single-use and expire. We never ask for a password.</span>
      </footer>
    </div>
  )
}
